import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";
import equipPoster from "@/assets/equip-poster.png";
import scoutImg from "@/assets/equip-scout.png";
import dubcircusImg from "@/assets/dubcircus.webp";
import { CaseStudyCard } from "./CaseStudyCard";

export function WorkIndex() {
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.05 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="frame bg-page-bg">
      <div className="container-narrow section-pad">
        <div
          className={cn(
            "transition-all duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
        >
          <span className="eyebrow">
            <span className="num">—</span> {t("work.index.eyebrow")}
          </span>
          <h1 className="section-title">
            <Trans
              i18nKey="work.index.title"
              components={{
                italic: <span className="serif-italic text-accent" />,
              }}
            />
          </h1>
          <p className="section-lede">{t("work.index.lede")}</p>
        </div>

        <div
          className={cn(
            "mt-14 grid auto-rows-fr grid-cols-1 gap-5 md:grid-cols-2 lg:gap-7",
            "transition-all delay-100 duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
        >
          <CaseStudyCard
            to={localizedHref("/work/equip")}
            image={equipPoster}
            video="https://equip.sport/wp-content/uploads/2024/05/equip-outro.mp4"
            client="Equip"
            type={t("work.equipCard.type")}
            taglineLead={t("work.equipCard.taglineLead")}
            taglineEm={t("work.equipCard.taglineEm")}
            desc={t("work.equipCard.desc")}
            stack={["Object detection", "Vision LLM", "TypeScript", "Fastify", "Postgres"]}
            meta={t("work.equipCard.meta")}
          />

          <CaseStudyCard
            to={localizedHref("/work/equip-scout")}
            image={scoutImg}
            imageAlt={t("work.scout.heroAlt")}
            client="Equip Scout"
            type={t("work.scoutCard.type")}
            taglineLead={t("work.scoutCard.taglineLead")}
            taglineEm={t("work.scoutCard.taglineEm")}
            desc={t("work.scoutCard.desc")}
            stack={["AI classification", "TypeScript", "React", "Mapbox"]}
            meta={t("work.scoutCard.meta")}
          />

          <CaseStudyCard
            to={localizedHref("/work/dubcircus")}
            image={dubcircusImg}
            imageAlt={t("work.dubcircus.heroAlt")}
            client="dubcircus"
            type={t("work.dubcircusCard.type")}
            taglineLead={t("work.dubcircusCard.taglineLead")}
            taglineEm={t("work.dubcircusCard.taglineEm")}
            desc={t("work.dubcircusCard.desc")}
            stack={["Voice separation", "Web Audio", "React", "Supabase Realtime", "Twitch chat"]}
            meta={t("work.dubcircusCard.meta")}
          />
        </div>
      </div>
    </section>
  );
}
